// Streaming ZIP writer + random-access ZIP reader for project files, stems, etc.
//
// Writer: entries are STORED (no compression — media is already compressed, and
// stored entries are what every ZIP tool and older WebCut versions read). The
// output is a Blob assembled from parts, so a multi-GB video is referenced, not
// copied into one giant buffer. ZIP64 records are added only when an entry or
// offset passes 4 GB, so normal-size files stay plain ZIP (readable by WebCut
// versions that used fflate's unzipSync).
//
// Reader: parses the central directory with Blob.slice, so opening a project
// never reads the whole file into memory; stored entries come back as zero-copy
// slices, deflated ones (older .dawproject / stems zips) are inflated.
import { inflateSync } from "fflate";

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; }
  return t;
})();
export function crc32(data: Uint8Array, crc = 0): number {
  let c = ~crc >>> 0;
  for (let i = 0; i < data.length; i++) c = CRC_TABLE[(c ^ data[i]) & 0xff] ^ (c >>> 8);
  return ~c >>> 0;
}
async function crc32Blob(blob: Blob, onBytes?: (n: number) => void): Promise<number> {
  const reader = blob.stream().getReader();
  let crc = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    crc = crc32(value, crc);
    onBytes?.(value.length);
  }
  return crc;
}

const MAX32 = 0xffffffff;
const enc = new TextEncoder();

function dosDateTime(d = new Date()): [number, number] {
  const time = (d.getHours() << 11) | (d.getMinutes() << 5) | (Math.floor(d.getSeconds() / 2));
  const date = ((Math.max(1980, d.getFullYear()) - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  return [time, date];
}

interface WrittenEntry { name: Uint8Array; crc: number; size: number; offset: number; time: number; date: number; }

export class ZipWriter {
  private parts: BlobPart[] = [];
  private entries: WrittenEntry[] = [];
  private offset = 0;
  /** "auto" (default): ZIP64 records only where sizes/offsets need them.
   *  "always": write them for every entry (for checking ZIP64 handling). */
  constructor(private zip64Mode: "auto" | "always" = "auto") {}

  /** Add a stored entry. Blobs are streamed once to compute the CRC, then
   *  referenced (not copied) in the output. */
  async add(name: string, data: Blob | Uint8Array | string, onBytes?: (n: number) => void): Promise<void> {
    const bytes = typeof data === "string" ? enc.encode(data) : data;
    const size = bytes instanceof Blob ? bytes.size : bytes.length;
    const crc = bytes instanceof Blob ? await crc32Blob(bytes, onBytes) : crc32(bytes);
    const nameBytes = enc.encode(name);
    const [time, date] = dosDateTime();
    const zip64 = this.zip64Mode === "always" || size >= MAX32 || this.offset >= MAX32;
    const extra = zip64 ? 20 : 0;
    const h = new DataView(new ArrayBuffer(30 + nameBytes.length + extra));
    h.setUint32(0, 0x04034b50, true);
    h.setUint16(4, zip64 ? 45 : 20, true);   // version needed
    h.setUint16(6, 0x0800, true);            // UTF-8 names
    h.setUint16(8, 0, true);                 // method: stored
    h.setUint16(10, time, true); h.setUint16(12, date, true);
    h.setUint32(14, crc, true);
    h.setUint32(18, zip64 ? MAX32 : size, true);
    h.setUint32(22, zip64 ? MAX32 : size, true);
    h.setUint16(26, nameBytes.length, true);
    h.setUint16(28, extra, true);
    new Uint8Array(h.buffer).set(nameBytes, 30);
    if (zip64) {
      const o = 30 + nameBytes.length;
      h.setUint16(o, 0x0001, true); h.setUint16(o + 2, 16, true);
      h.setBigUint64(o + 4, BigInt(size), true); h.setBigUint64(o + 12, BigInt(size), true);
    }
    this.entries.push({ name: nameBytes, crc, size, offset: this.offset, time, date });
    this.parts.push(h.buffer, bytes as BlobPart);
    this.offset += h.byteLength + size;
  }

  /** Write the central directory and return the finished archive. */
  finish(type = "application/zip"): Blob {
    const cdStart = this.offset;
    let cdSize = 0;
    const always = this.zip64Mode === "always";
    for (const e of this.entries) {
      const needSize = always || e.size >= MAX32, needOff = always || e.offset >= MAX32;
      const extraLen = needSize || needOff ? 4 + (needSize ? 16 : 0) + (needOff ? 8 : 0) : 0;
      const c = new DataView(new ArrayBuffer(46 + e.name.length + extraLen));
      c.setUint32(0, 0x02014b50, true);
      c.setUint16(4, 45, true);                                  // version made by
      c.setUint16(6, extraLen ? 45 : 20, true);                  // version needed
      c.setUint16(8, 0x0800, true);
      c.setUint16(10, 0, true);
      c.setUint16(12, e.time, true); c.setUint16(14, e.date, true);
      c.setUint32(16, e.crc, true);
      c.setUint32(20, needSize ? MAX32 : e.size, true);
      c.setUint32(24, needSize ? MAX32 : e.size, true);
      c.setUint16(28, e.name.length, true);
      c.setUint16(30, extraLen, true);
      c.setUint32(42, needOff ? MAX32 : e.offset, true);
      new Uint8Array(c.buffer).set(e.name, 46);
      if (extraLen) {
        let o = 46 + e.name.length;
        c.setUint16(o, 0x0001, true); c.setUint16(o + 2, extraLen - 4, true); o += 4;
        if (needSize) { c.setBigUint64(o, BigInt(e.size), true); c.setBigUint64(o + 8, BigInt(e.size), true); o += 16; }
        if (needOff) c.setBigUint64(o, BigInt(e.offset), true);
      }
      this.parts.push(c.buffer);
      cdSize += c.byteLength;
    }
    const n = this.entries.length;
    const needZip64 = always || n >= 0xffff || cdStart >= MAX32 || cdSize >= MAX32;
    if (needZip64) {
      const z = new DataView(new ArrayBuffer(56 + 20));
      z.setUint32(0, 0x06064b50, true); z.setBigUint64(4, 44n, true);
      z.setUint16(12, 45, true); z.setUint16(14, 45, true);
      z.setBigUint64(24, BigInt(n), true); z.setBigUint64(32, BigInt(n), true);
      z.setBigUint64(40, BigInt(cdSize), true); z.setBigUint64(48, BigInt(cdStart), true);
      // locator
      z.setUint32(56, 0x07064b50, true); z.setBigUint64(64, BigInt(cdStart + cdSize), true); z.setUint32(72, 1, true);
      this.parts.push(z.buffer);
    }
    const end = new DataView(new ArrayBuffer(22));
    end.setUint32(0, 0x06054b50, true);
    end.setUint16(8, Math.min(n, 0xffff), true); end.setUint16(10, Math.min(n, 0xffff), true);
    end.setUint32(12, Math.min(cdSize, MAX32), true);
    end.setUint32(16, needZip64 ? MAX32 : cdStart, true);
    this.parts.push(end.buffer);
    return new Blob(this.parts, { type });
  }
}

// ---- reader -------------------------------------------------------------------
export interface ZipEntry { name: string; method: number; csize: number; usize: number; crc: number; localOffset: number; }

const u8 = async (b: Blob) => new Uint8Array(await b.arrayBuffer());
const dec = new TextDecoder();

/** Index a ZIP by its central directory (reads only the tail + directory). */
export async function readZip(file: Blob): Promise<Map<string, ZipEntry>> {
  const tailLen = Math.min(file.size, 65557);
  const tail = await u8(file.slice(file.size - tailLen));
  const tv = new DataView(tail.buffer);
  let eocd = -1;
  for (let i = tail.length - 22; i >= 0; i--) if (tv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  if (eocd < 0) throw new Error("Not a ZIP archive (no end-of-directory record).");
  let count = tv.getUint16(eocd + 10, true);
  let cdSize = tv.getUint32(eocd + 12, true);
  let cdOff = tv.getUint32(eocd + 16, true);
  if (count === 0xffff || cdSize === MAX32 || cdOff === MAX32) {
    // ZIP64: the locator sits just before the end record
    const locAt = file.size - tailLen + eocd - 20;
    const loc = new DataView((await u8(file.slice(locAt, locAt + 20))).buffer);
    if (loc.getUint32(0, true) !== 0x07064b50) throw new Error("Corrupt ZIP64 archive (missing locator).");
    const z64At = Number(loc.getBigUint64(8, true));
    const z = new DataView((await u8(file.slice(z64At, z64At + 56))).buffer);
    if (z.getUint32(0, true) !== 0x06064b50) throw new Error("Corrupt ZIP64 archive (bad end record).");
    count = Number(z.getBigUint64(32, true));
    cdSize = Number(z.getBigUint64(40, true));
    cdOff = Number(z.getBigUint64(48, true));
  }
  if (cdOff + cdSize > file.size) throw new Error("The file is truncated (incomplete download or save?).");
  const cd = await u8(file.slice(cdOff, cdOff + cdSize));
  const v = new DataView(cd.buffer);
  const out = new Map<string, ZipEntry>();
  let p = 0;
  for (let i = 0; i < count && p + 46 <= cd.length; i++) {
    if (v.getUint32(p, true) !== 0x02014b50) throw new Error("Corrupt ZIP central directory.");
    const method = v.getUint16(p + 10, true);
    const crc = v.getUint32(p + 16, true);
    let csize = v.getUint32(p + 20, true), usize = v.getUint32(p + 24, true);
    const nameLen = v.getUint16(p + 28, true), extraLen = v.getUint16(p + 30, true), commentLen = v.getUint16(p + 32, true);
    let localOffset = v.getUint32(p + 42, true);
    const name = dec.decode(cd.subarray(p + 46, p + 46 + nameLen));
    // ZIP64 extra: only the fields that were 0xFFFFFFFF, in order usize, csize, offset
    let e = p + 46 + nameLen;
    const eEnd = e + extraLen;
    while (e + 4 <= eEnd) {
      const id = v.getUint16(e, true), len = v.getUint16(e + 2, true);
      if (id === 0x0001) {
        let q = e + 4;
        if (usize === MAX32) { usize = Number(v.getBigUint64(q, true)); q += 8; }
        if (csize === MAX32) { csize = Number(v.getBigUint64(q, true)); q += 8; }
        if (localOffset === MAX32) { localOffset = Number(v.getBigUint64(q, true)); }
      }
      e += 4 + len;
    }
    out.set(name, { name, method, csize, usize, crc, localOffset });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}

/** An entry's contents as a Blob (a zero-copy slice for stored entries). */
export async function zipEntryBlob(file: Blob, e: ZipEntry, type = ""): Promise<Blob> {
  const lh = new DataView((await u8(file.slice(e.localOffset, e.localOffset + 30))).buffer);
  if (lh.getUint32(0, true) !== 0x04034b50) throw new Error(`Corrupt ZIP entry header for ${e.name}.`);
  const start = e.localOffset + 30 + lh.getUint16(26, true) + lh.getUint16(28, true);
  if (start + e.csize > file.size) throw new Error(`${e.name} is truncated.`);
  const raw = file.slice(start, start + e.csize, type);
  if (e.method === 0) return raw;
  if (e.method === 8) return new Blob([inflateSync(await u8(raw)) as BlobPart], { type });
  throw new Error(`${e.name} uses an unsupported ZIP compression method (${e.method}).`);
}

/** An entry's bytes, CRC-checked (use for small entries like project.json). */
export async function zipEntryBytes(file: Blob, e: ZipEntry): Promise<Uint8Array> {
  const bytes = await u8(await zipEntryBlob(file, e));
  if (crc32(bytes) !== e.crc) throw new Error(`${e.name} is damaged (checksum mismatch).`);
  return bytes;
}
